import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useFeatureConfig } from '../../../context/FeatureConfigContext';
import { useTCoins } from '../../../context/TCoinsContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { getMascotByXP } from '../../../data/nurseAppModels';
import LanguageSwitcher from '../../LanguageSwitcher';
import NotificationBell from '../../Notification/NotificationBell';
import SearchBar from '../../Layout/SearchBar';
import { useTheme } from '../../../context/ThemeContext';
import TCoinsWallet from '../../TCoins/TCoinsWallet';
import TeamSelector from '../Team/TeamSelector';

const NurseTopNavbar = () => {
  const { currentUser } = useAuth();
  const { isFeatureEnabled } = useFeatureConfig();
  const { getTotalTCoins } = useTCoins();
  const navigate = useNavigate();
  const [mascot, setMascot] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [hasDailyBonus, setHasDailyBonus] = useState(false);
  const [showTCoinsWallet, setShowTCoinsWallet] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  // Prevent body scroll when menu is open
  React.useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileMenu]);

  React.useEffect(() => {
    if (currentUser) {
      fetchUserMascot();
      checkDailyBonus();
    }
  }, [currentUser]);

  // Listen for TCoinsWallet open event
  useEffect(() => {
    const handleOpenTCoinsWallet = () => {
      setShowTCoinsWallet(true);
      setShowMobileMenu(false); // Close mobile menu when opening wallet
    };
    
    window.addEventListener('openTCoinsWallet', handleOpenTCoinsWallet);
    
    return () => {
      window.removeEventListener('openTCoinsWallet', handleOpenTCoinsWallet);
    };
  }, []);

  // Listen for notification dropdown state changes
  useEffect(() => {
    const handleNotificationOpen = () => {
      setIsNotificationOpen(true);
    };
    
    const handleNotificationClose = () => {
      setIsNotificationOpen(false);
    };
    
    window.addEventListener('notificationDropdownOpen', handleNotificationOpen);
    window.addEventListener('notificationDropdownClose', handleNotificationClose);
    
    return () => {
      window.removeEventListener('notificationDropdownOpen', handleNotificationOpen);
      window.removeEventListener('notificationDropdownClose', handleNotificationClose);
    };
  }, []);

  const checkDailyBonus = async () => {
    try {
      if (!currentUser?.uid) return;
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const lastDailyBonus = userData.lastDailyBonus?.toDate?.();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const hasClaimedToday = lastDailyBonus && lastDailyBonus >= today;
        setHasDailyBonus(!hasClaimedToday);
      }
    } catch (error) {
      console.error('Error checking daily bonus:', error);
    }
  };

  const fetchUserMascot = async () => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        const xp = data.currentXP || 0;
        const mascotData = getMascotByXP(xp);
        setMascot(mascotData);
      }
    } catch (error) {
      console.error('Error fetching mascot:', error);
    }
  };

  const { currentTheme } = useTheme();
  const gradientClass = `bg-gradient-to-r ${currentTheme.colors.gradient || 'from-purple-500 to-pink-500'}`;

  return (
    <nav className={`${gradientClass} text-white shadow-lg sticky top-0 z-[100]`} style={{
      background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`
    }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 md:py-5">
        <div className="flex items-center justify-between">
          {/* Left: Logo + Name or Profile */}
          {currentUser ? (
            <div 
              className="flex items-center space-x-3 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/nurse')}
            >
              <img 
                src="/logo.png" 
                alt="NurseApp Logo" 
                className="w-10 h-10 rounded-lg object-contain shadow-lg bg-white/20 p-1.5 transition-all duration-300 hover:scale-110 hover:shadow-xl hover:bg-white/30"
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                }}
                onError={(e) => {
                  // Fallback to emoji if image not found
                  e.target.style.display = 'none';
                  if (!e.target.nextSibling) {
                    const fallback = document.createElement('div');
                    fallback.className = 'w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center shadow-md';
                    fallback.innerHTML = '<span class="text-2xl">🩺</span>';
                    e.target.parentNode.insertBefore(fallback, e.target.nextSibling);
                  }
                }}
              />
              <div>
                <p className="text-sm font-bold text-white">NurseApp</p>
                <p className="text-xs text-white/90">
                  {currentUser.displayName || 'ผู้ใช้'}
                </p>
              </div>
            </div>
          ) : (
            <Link 
              to="/" 
              className="flex items-center space-x-3 sm:space-x-4 flex-shrink-0 hover:opacity-80 transition-opacity"
            >
              <img 
                src="/logo.png" 
                alt="NurseApp Logo" 
                className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg object-contain shadow-lg bg-white/20 p-1.5 sm:p-2 transition-all duration-300 hover:scale-110 hover:shadow-xl hover:bg-white/30"
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                }}
                onError={(e) => {
                  // Fallback to emoji if image not found
                  e.target.style.display = 'none';
                  if (!e.target.nextSibling) {
                    const fallback = document.createElement('div');
                    fallback.className = 'w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg bg-blue-500 flex items-center justify-center shadow-md';
                    fallback.innerHTML = '<span class="text-2xl sm:text-3xl md:text-4xl">🩺</span>';
                    e.target.parentNode.insertBefore(fallback, e.target.nextSibling);
                  }
                }}
              />
              <div>
                <p className="text-base sm:text-lg md:text-xl font-bold text-white">NurseApp</p>
              </div>
            </Link>
          )}

          {/* Right: Auth Buttons (if not logged in) or Menu (if logged in) */}
          {!currentUser ? (
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Language Switcher */}
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>
              
              {/* Login Button */}
              <Link
                to="/login"
                className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base font-medium text-white hover:bg-white/20 rounded-lg transition"
              >
                เข้าสู่ระบบ
              </Link>
              
              {/* Register Button */}
              <Link
                to="/register"
                className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base font-medium bg-white text-blue-600 rounded-lg hover:bg-white/90 transition shadow-md"
              >
                ลงทะเบียน
              </Link>
            </div>
          ) : (
            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 relative">
              {/* Team Selector */}
              <TeamSelector />

              {/* Level Display */}
              {mascot && (
                <div className="text-right mr-2 hidden md:block">
                  <p className="text-xs sm:text-sm text-white/90">
                    Level {mascot.level}: {mascot.nameThai}
                  </p>
                </div>
              )}

              {/* Language Switcher */}
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>

              {/* Search Icon */}
              <button
                onClick={() => navigate('/nurse/search')}
                className="p-2 sm:p-2.5 hover:bg-white/20 rounded-lg transition"
                title="ค้นหา"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              {/* Notification Bell */}
              <div className="relative z-10">
                <NotificationBell />
              </div>

              {/* Hamburger Menu - Hide when notification dropdown is open */}
              {!isNotificationOpen && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowMobileMenu(prev => !prev);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  className="block p-2 sm:p-2.5 hover:bg-white/20 rounded-lg transition relative z-[1000] cursor-pointer"
                  style={{ 
                    pointerEvents: 'auto',
                    position: 'relative',
                    zIndex: 1000,
                    display: 'block'
                  }}
                  title="เมนู"
                  type="button"
                  aria-label="เมนู"
                  aria-expanded={showMobileMenu}
                >
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Menu Overlay (Mobile & Desktop) */}
        {showMobileMenu && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
              onClick={() => setShowMobileMenu(false)}
              style={{ top: 0 }}
            />
            
            {/* Menu Drawer */}
            <div 
              className="fixed inset-y-0 right-0 w-80 z-[9999] shadow-xl overflow-y-auto"
              style={{
                background: `linear-gradient(to bottom, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`,
                top: 0
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-white/20">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-bold text-white">เมนู</h2>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowMobileMenu(false);
                    }}
                    className="p-1 hover:bg-white/20 rounded transition"
                    aria-label="ปิดเมนู"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div 
                  className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => {
                    navigate('/nurse');
                    setShowMobileMenu(false);
                  }}
                >
                  <img 
                    src="/logo.png" 
                    alt="NurseApp Logo" 
                    className="w-12 h-12 rounded-lg object-contain shadow-lg bg-white/20 p-1.5 transition-all duration-300"
                    style={{
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (!e.target.nextSibling) {
                        const fallback = document.createElement('div');
                        fallback.className = 'w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center shadow-md';
                        fallback.innerHTML = '<span class="text-2xl">🩺</span>';
                        e.target.parentNode.insertBefore(fallback, e.target.nextSibling);
                      }
                    }}
                  />
                  <div>
                    <p className="text-base font-bold text-white">NurseApp</p>
                    <p className="text-xs text-white/90">
                      {currentUser?.displayName || 'ผู้ใช้'}
                    </p>
                  </div>
                </div>

                {/* T Coins Display */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.dispatchEvent(new CustomEvent('openTCoinsWallet'));
                    setShowMobileMenu(false);
                  }}
                  className="relative mt-3 w-full flex items-center space-x-2 text-yellow-300 hover:text-yellow-200 hover:bg-white hover:bg-opacity-10 rounded-lg p-2 transition-colors cursor-pointer"
                >
                  <span className="text-xl">🪙</span>
                  <span className="font-bold text-lg">{getTotalTCoins().toLocaleString()}</span>
                  <span className="text-sm text-white text-opacity-80">T Coins</span>
                  {hasDailyBonus && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold animate-pulse">
                      🎁
                    </span>
                  )}
                </button>
              </div>

              {/* Menu Items */}
              <div className="p-4 flex flex-col space-y-1">
                <Link
                  to="/nurse"
                  onClick={() => setShowMobileMenu(false)}
                  className="px-4 py-3 hover:bg-white/20 rounded-lg transition flex items-center space-x-3 text-white"
                >
                  <span className="text-xl">🏠</span>
                  <span className="font-medium">หน้าแรก</span>
                </Link>
                <Link
                  to="/nurse/shifts"
                  onClick={() => setShowMobileMenu(false)}
                  className="px-4 py-3 hover:bg-white/20 rounded-lg transition flex items-center space-x-3 text-white"
                >
                  <span className="text-xl">📅</span>
                  <span className="font-medium">ตารางเวร</span>
                </Link>
                <Link
                  to="/nurse/equipment"
                  onClick={() => setShowMobileMenu(false)}
                  className="px-4 py-3 hover:bg-white/20 rounded-lg transition flex items-center space-x-3 text-white"
                >
                  <span className="text-xl">🩺</span>
                  <span className="font-medium">อุปกรณ์</span>
                </Link>
                <Link
                  to="/nurse/teams"
                  onClick={() => setShowMobileMenu(false)}
                  className="px-4 py-3 hover:bg-white/20 rounded-lg transition flex items-center space-x-3 text-white"
                >
                  <span className="text-xl">👥</span>
                  <span className="font-medium">ทีม</span>
                </Link>
                {isFeatureEnabled('marketplace') && (
                  <Link
                    to="/marketplace"
                    onClick={() => setShowMobileMenu(false)}
                    className="px-4 py-3 hover:bg-white/20 rounded-lg transition flex items-center space-x-3 text-white"
                  >
                    <span className="text-xl">🛒</span>
                    <span className="font-medium">Marketplace</span>
                  </Link>
                )}
                <Link
                  to="/nurse/profile"
                  onClick={() => setShowMobileMenu(false)}
                  className="px-4 py-3 hover:bg-white/20 rounded-lg transition flex items-center space-x-3 text-white"
                >
                  <span className="text-xl">👤</span>
                  <span className="font-medium">Profile</span>
                </Link>
                <div className="border-t border-white/20 my-2"></div>
                <Link
                  to="/nurse/settings"
                  onClick={() => setShowMobileMenu(false)}
                  className="px-4 py-3 hover:bg-white/20 rounded-lg transition flex items-center space-x-3 text-white"
                >
                  <span className="text-xl">⚙️</span>
                  <span className="font-medium">ตั้งค่า</span>
                </Link>
              </div>
            </div>
          </>
        )}

        {/* T Coins Wallet Modal */}
        {showTCoinsWallet && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
            onClick={(e) => {
              // Close when clicking backdrop
              if (e.target === e.currentTarget) {
                setShowTCoinsWallet(false);
              }
            }}
          >
            <div 
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <TCoinsWallet onClose={() => setShowTCoinsWallet(false)} />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NurseTopNavbar;

